import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';

// Type definitions for File System Access API
interface FileSystemWritableFileStream extends WritableStream {
  write(data: string | BufferSource | Blob): Promise<void>;
  close(): Promise<void>;
}

interface FileSystemFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>;
}

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
}

declare global {
  interface Window {
    showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;
  }
}

export interface CSVExportOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  columns: GridColDef[];
  columnVisibilityModel: Record<string, boolean>;
  filename: string;
  separator?: string;
}

/**
 * Exports visible table data to CSV format with user-selectable save location
 * @param options Export configuration options
 */
export const exportToCSV = async ({
  data,
  columns,
  columnVisibilityModel,
  filename,
  separator = ';',
}: CSVExportOptions): Promise<void> => {
  // Filter columns based on visibility model
  const visibleColumns = columns.filter(col => {
    // If column visibility is not explicitly set, consider it visible
    return columnVisibilityModel[col.field] !== false;
  });

  // Filter out action columns (columns without headerName or with renderCell for actions)
  const exportableColumns = visibleColumns.filter(col => 
    col.headerName && !col.field.toLowerCase().includes('actions')
  );

  if (exportableColumns.length === 0) {
    console.warn('No visible columns to export');
    return;
  }

  // Create CSV header row
  const headers = exportableColumns.map(col => escapeCSVValue(col.headerName || col.field));
  const csvRows = [headers.join(separator)];

  // Create CSV data rows
  data.forEach(row => {
    const values = exportableColumns.map(col => {
      let value = row[col.field];

      // Handle valueGetter if present (skip complex valueGetters for simplicity)
      if (col.valueGetter && typeof col.valueGetter === 'function') {
        try {
          // Try to call valueGetter, but if it fails, use the raw field value
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          value = (col.valueGetter as any)(value, row);
        } catch {
          // If valueGetter fails, use the raw value
          console.debug(`Using raw value for column ${col.field}`);
        }
      }

      // Handle different value types
      if (value === null || value === undefined) {
        return '';
      }

      // Handle objects and arrays (convert to JSON string)
      if (typeof value === 'object') {
        return escapeCSVValue(JSON.stringify(value));
      }

      // Handle dates
      if (value instanceof Date) {
        return escapeCSVValue(value.toISOString());
      }

      return escapeCSVValue(String(value));
    });

    csvRows.push(values.join(separator));
  });

  // Create CSV content
  const csvContent = csvRows.join('\n');

  // Create blob
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });

  // Try to use File System Access API for save location selection
  if (window.showSaveFilePicker) {
    try {
      // Show save file dialog
      const handle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'CSV Files',
            accept: { 'text/csv': ['.csv'] },
          },
        ],
      });

      // Write the file
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      
      console.log('CSV file saved successfully');
      return;
    } catch (error) {
      // User cancelled or error occurred
      if ((error as Error).name === 'AbortError') {
        console.log('User cancelled file save');
        return;
      }
      console.warn('File System Access API failed, falling back to traditional download:', error);
    }
  }

  // Fallback to traditional download (for browsers that don't support File System Access API)
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/**
 * Escapes special characters in CSV values
 * @param value Value to escape
 * @returns Escaped value
 */
const escapeCSVValue = (value: string): string => {
  // If value contains separator, quotes, or newlines, wrap it in quotes
  if (value.includes(';') || value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    // Escape existing quotes by doubling them
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

/**
 * Generates a filename with timestamp
 * @param prefix Filename prefix
 * @param page Current page number (optional)
 * @returns Generated filename
 */
export const generateCSVFilename = (prefix: string): string => {
  const date = new Date();
  const dateStr = date.toISOString().split('T')[0].replace(/-/g, '');
  return `${prefix}_${dateStr}.csv`;
};
